import { useCallback } from 'react';
import { useTranslation } from '../contexts/TranslationContext';
import { humanizeDataValue } from '../utils/dataLabels';

/**
 * `AnalyticsEvent.deviceType` is a nullable free-text `String?`
 * (`schema.prisma:490`) validated only by `@IsString()`, written verbatim from
 * the request and read back by a `groupBy` over whatever distinct strings the
 * column holds. The single known producer is the landing page's tracker
 * (`flowsoft-landing/analytics.js`), which emits exactly these three values —
 * so the dictionary covers what is actually produced, and the fallback covers
 * the fact that nothing in the schema or the ingest path enforces it.
 */
const DEVICE_TYPE_LABEL_KEYS: Record<string, string> = {
  mobile: 'common.deviceTypes.mobile',
  desktop: 'common.deviceTypes.desktop',
  tablet: 'common.deviceTypes.tablet',
};

export function useDeviceTypeLabel() {
  const { t } = useTranslation();

  return useCallback(
    (deviceType: string): string => {
      const normalized = deviceType.trim().toLowerCase();
      if (!normalized) {
        return t('common.deviceTypes.unknown');
      }

      const key = DEVICE_TYPE_LABEL_KEYS[normalized];
      return key ? t(key) : humanizeDataValue(normalized);
    },
    [t],
  );
}
